<script setup lang="ts">
import {
  DataPointDetailsDto,
  EvaluationParameterDto,
  instanceOfEnumerationEvaluationResultDto,
  instanceOfManualTaskWorkResultDto,
  instanceOfNumericEvaluationParameterDto,
  instanceOfNumericEvaluationResultDto,
  ManualTaskWorkResultDto,
} from '@/api/generator';
import EvaluationState from '@/components/evaluation/EvaluationState.vue';
import LinearGauge from '@/components/charts/LinearGauge.vue';
import FormLabel from '@/components/FormLabel/FormLabel.vue';
import EvaluationResultHistory from '@/components/results/EvaluationResultHistory.vue';
import { computed, watch } from 'vue';
import { useToggle } from '@vueuse/core';
import { getLimitedValue } from '@/components/charts/utils/limitedValue';
import { toDisplayValue, getFormattingOptionsFromEvaluationParameter } from '@/utils/numberUtils';
import { isNumber } from 'lodash';
import { useTranslation } from 'i18next-vue';

const { t } = useTranslation();

const props = defineProps<{
  result: ManualTaskWorkResultDto | DataPointDetailsDto;
  evaluation: EvaluationParameterDto;
  ignored?: boolean;
  hideHistory?: boolean;
}>();

const emit = defineEmits<{
  enableEditing: [];
  disableEditing: [];
}>();

const evaluationResult = computed(() => props.result.evaluationResult ?? undefined);

const isIgnored = computed(
  () =>
    props.ignored ||
    (instanceOfManualTaskWorkResultDto(props.result) && props.result.state === 'Ignored'),
);

const limitedValue = computed(() =>
  evaluationResult.value &&
  instanceOfNumericEvaluationResultDto(evaluationResult.value) &&
  instanceOfNumericEvaluationParameterDto(props.evaluation)
    ? getLimitedValue(evaluationResult.value, props.evaluation)
    : undefined,
);

const [inEditing, toggleInEditing] = useToggle(false);

watch(inEditing, (isEditing: boolean) => {
  if (isEditing) {
    emit('enableEditing');
  } else {
    emit('disableEditing');
  }
});

const label = computed(() =>
  instanceOfEnumerationEvaluationResultDto(evaluationResult.value) ? t('ManualInput') : t('Value'),
);
</script>

<template>
  <div>
    <div class="w-100 d-flex me-auto justify-end align-start">
      <div v-if="inEditing" class="flex-grow-1 mr-2">
        <slot
          name="edit"
          :toggleInEditing="toggleInEditing"
          :inEditing="inEditing"
          :evaluationResult="evaluationResult"
        />
      </div>

      <div v-else class="mb-2 flex-grow-1">
        <FormLabel :label="label">
          <div
            class="d-flex align-center"
            :class="{
              'w-25': evaluationResult && instanceOfNumericEvaluationResultDto(evaluationResult),
            }"
          >
            <div
              v-if="evaluationResult && instanceOfEnumerationEvaluationResultDto(evaluationResult)"
            >
              {{ evaluationResult.selectedOption?.name }}
            </div>

            <div
              v-if="
                evaluationResult &&
                instanceOfNumericEvaluationResultDto(evaluationResult) &&
                isNumber(evaluationResult.deviationValue)
              "
            >
              {{
                toDisplayValue(
                  evaluationResult.deviationValue,
                  getFormattingOptionsFromEvaluationParameter(evaluation),
                )
              }}
            </div>

            <slot name="value-append" />
          </div>
        </FormLabel>
      </div>

      <div
        v-if="
          limitedValue &&
          evaluationResult &&
          instanceOfNumericEvaluationResultDto(evaluationResult) &&
          evaluationResult?.evaluationOutcome !== 'WithoutEvaluation'
        "
        class="ml-6 mr-6 mt-6 limitsGauge flex-grow-1"
        style="min-height: 44px"
      >
        <LinearGauge
          :value="limitedValue"
          :disabled="isIgnored || inEditing"
          :unit="evaluationResult?.deviationUnit ?? undefined"
        />
      </div>

      <div class="mr-3 d-flex align-end flex-shrink-0">
        <div class="mb-2 d-flex flex-column align-center justify-center">
          <v-label class="d-block">{{ $t('Result_one') }}</v-label>
          <EvaluationState
            :value="result.evaluationResult?.evaluationOutcome || undefined"
            :disabled="isIgnored || inEditing"
          />
        </div>

        <div v-if="evaluationResult">
          <slot
            name="actions"
            :evaluationResult="evaluationResult"
            :toggleInEditing="toggleInEditing"
            :inEditing="inEditing"
            :disabled="inEditing"
            :isResultIgnored="isIgnored"
          />
        </div>
      </div>
    </div>

    <EvaluationResultHistory v-if="!hideHistory" :value="result" />
  </div>
</template>

<style scoped lang="scss">
.limitsGauge {
  min-width: 100px;
}
</style>
